import React from 'react';
import { Image, Typography, Tag, Tooltip } from 'antd';
import { QuoteLineItemProps } from '@partsbadger/types';
import { Price } from '@partsbadger/library';
import { InfoCircleOutlined } from '@ant-design/icons';
import { isLineItemAnalyzing } from '../../lineItemAnalysis';

const { Text } = Typography;

type IPartItemProps = {
    part: QuoteLineItemProps;
    isComplete: boolean;
    onClick?: (part: QuoteLineItemProps) => void;
    priceFormatter?: (value?: number) => React.ReactNode;
    variantName?: string | null;
};

const freqLabel = (freq?: string | null) => {
    switch (freq) {
        case 'monthly':
            return 'Monthly';
        case 'quarterly':
            return 'Quarterly';
        case 'yearly':
            return 'Yearly';
        default:
            return null; // includes 'one-time' / undefined
    }
};

export default function PartItem({ part, isComplete, variantName }: IPartItemProps) {
    const freq = freqLabel((part as any)?.quantity_frequency);
    // Only surface a price when the item is genuinely safe to auto-quote. Gating
    // on needs_manual_quote alone let a stale safe_auto_quote leak an instant
    // price after brep-explorer failed (PTB-6278).
    // While analysis runs, the price is provisional: the default lead time is
    // re-selected once analysis completes (PTB-6983).
    const isAnalyzing = isLineItemAnalyzing(part);
    const isSafeAutoQuote = Boolean(part?.safe_auto_quote) && !part?.needs_manual_quote && !isAnalyzing;
    const isPricingInProgress = isAnalyzing || (!isSafeAutoQuote && Boolean(part?.is_pricing_in_progress));
    const showUnit = isSafeAutoQuote;
    const outcomeKind = !isComplete
        ? 'pending'
        : isSafeAutoQuote
        ? 'auto-price'
        : isPricingInProgress
        ? 'calculating'
        : 'manual-quote';

    return (
        <div key={part.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex-1 min-w-0">
                <div className="flex flex-row space-x-2">
                    {part?.product?.thumbnail ? (
                        <div
                            className="rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-100"
                            style={{ width: 30, height: 30 }}
                        >
                            <Image
                                alt={part?.product?.attachments?.file3d?.filename || part.name}
                                src={part.product.thumbnail}
                                width={25}
                                height={25}
                                preview={false}
                            />
                        </div>
                    ) : null}
                    <div>
                        <div className="part-name-wrapper">
                            <p className="part-name part-name--quote-summary">{part.name}</p>
                        </div>
                        {variantName ? (
                            <Tag style={{ margin: 0 }} color="geekblue">
                                {variantName}
                            </Tag>
                        ) : null}
                        {isComplete ? (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                                <span>Qty: {part.quantity}</span>

                                {freq && (
                                    <Tooltip
                                        title={
                                            <div className="text-sm">
                                                <strong>Frequency</strong>
                                                <ul className="mt-1 list-disc list-inside">
                                                    <li>{freq}</li>
                                                </ul>
                                            </div>
                                        }
                                        overlayInnerStyle={{
                                            backgroundColor: '#fff',
                                            color: '#000',
                                            borderRadius: 8,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            padding: '10px 12px',
                                            maxWidth: 260,
                                        }}
                                    >
                                        <span className="inline-flex">
                                            <InfoCircleOutlined className="text-slate-400 hover:text-slate-600" />
                                        </span>
                                    </Tooltip>
                                )}

                                {showUnit && (
                                    <>
                                        <span>•</span>
                                        <span className="font-semibold">
                                            <Price value={part?.unit_price} /> ea.
                                        </span>
                                    </>
                                )}
                            </p>
                        ) : (
                            <p className="text-xs text-amber-600">Configuration incomplete</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="text-right" data-testid="rapid-quote-first-outcome" data-outcome-kind={outcomeKind}>
                {isComplete ? (
                    isSafeAutoQuote ? (
                        <div className="text-sm font-semibold text-slate-900">
                            <Price value={part?.total_price} />
                        </div>
                    ) : isPricingInProgress ? (
                        <Text type="secondary" className="text-xs">
                            Calculating…
                        </Text>
                    ) : (
                        <Tag color="blue">Manual quote required</Tag>
                    )
                ) : (
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                )}
            </div>
        </div>
    );
}
